import { Button, Notification, Typography } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { CardContent } from '@hero/atoms';
import { MissingRecipientInfoModal } from '@hero/components/modals';
import { AUTOMATION_STATUSES, CONTACT_TYPE } from '@hero/constants';
import { useGetTagBasedAutomationRecipient } from '@hero/hooks';
import { useGetAutomation, useUpdateAutomation } from '@hero/services';

import * as S from '../style';

import { WithContacts } from './WithContacts';
import { WithTags } from './WithTags';

function AutomationReviewContactsAccordion() {
  const { t } = useTranslation();
  const { code } = useParams();
  const { data: automation } = useGetAutomation({ code });
  const { mutateAsync: onUpdate } = useUpdateAutomation();
  const [isMissingInfoModalOpen, setIsMissingInfoModalOpen] = useState(false);

  const { missingInfoContacts } = useGetTagBasedAutomationRecipient(automation);

  const formik = useFormik({
    initialValues: {
      sharingMethod: automation?.contacts?.ids?.length ? CONTACT_TYPE.list : CONTACT_TYPE.tags
    },
    enableReinitialize: true,
    validateOnMount: true
  });

  const addingMethods = {
    [CONTACT_TYPE.tags]: {
      badge: !automation?.contacts?.tags?.length ? (
        <Trans
          i18nKey="badgeIncludeAtLeastOneTagToActivate"
          values={{
            featureType: t('automation').toLocaleLowerCase(),
            contactType: t('tag').toLocaleLowerCase()
          }}
          components={{ b: <b /> }}
        />
      ) : (
        ''
      )
    },
    [CONTACT_TYPE.list]: {
      badge: !automation?.contacts?.ids?.length ? (
        <Trans
          i18nKey="badgeIncludeAtLeastOneTagToActivate"
          values={{
            featureType: t('automation').toLocaleLowerCase(),
            contactType: t('recipient').toLocaleLowerCase()
          }}
          components={{ b: <b /> }}
        />
      ) : (
        ''
      )
    }
  };

  const hasContanctsIds = automation?.contacts?.ids?.length > 0;
  const hasContanctsTags = automation?.contacts?.tags?.length > 0;
  const missingInfoContactsLength = missingInfoContacts?.length > 0;
  const isMissingInfoContacts = hasContanctsIds && missingInfoContactsLength;
  const isMissingInfoTags = hasContanctsTags && missingInfoContactsLength;
  const isMissingInfo = isMissingInfoContacts || isMissingInfoTags;

  return (
    <>
      <CardContent>
        {isMissingInfo && (
          <Notification
            status="info"
            style={{ marginBottom: 24 }}
            message={
              <Trans
                i18nKey="automationContactMissingDatesWarning"
                values={{
                  count: missingInfoContacts?.length,
                  type:
                    automation?.type === 'birthday'
                      ? t('birthdays').toLocaleLowerCase()
                      : t('workAnniversaries').toLocaleLowerCase()
                }}
                components={{
                  b: <b />,
                  button: (
                    <Button
                      variant="link"
                      color="secondary"
                      onClick={() => setIsMissingInfoModalOpen(true)}
                    />
                  )
                }}
              />
            }
          />
        )}
        <S.AddingMethods>
          <S.AddingMethodsBody>
            <S.AddingMethodsTitle variant="body1Bold" color="fg">
              {t('includedVia')}:
            </S.AddingMethodsTitle>
            <S.AddingMethodsList>
              <S.AddingMethodsListItem
                id={CONTACT_TYPE.list}
                name="sharingMethod"
                value={CONTACT_TYPE.list}
                label={t('contactList')}
                checked={formik.values.sharingMethod === CONTACT_TYPE.list}
                disabled={automation?.status !== AUTOMATION_STATUSES.draft}
                onChange={async () => {
                  formik.setFieldValue('sharingMethod', CONTACT_TYPE.list);
                  await onUpdate({
                    params: { code },
                    body: {
                      contacts: {
                        ids: [],
                        tags: []
                      }
                    }
                  });
                }}
              />
              <S.AddingMethodsListItem
                id={CONTACT_TYPE.tags}
                name="sharingMethod"
                value={CONTACT_TYPE.tags}
                checked={formik.values.sharingMethod === CONTACT_TYPE.tags}
                disabled={automation?.status !== AUTOMATION_STATUSES.draft}
                label={t('tags')}
                onChange={async () => {
                  formik.setFieldValue('sharingMethod', CONTACT_TYPE.tags);
                  await onUpdate({
                    params: { code },
                    body: {
                      contacts: {
                        ids: [],
                        tags: []
                      }
                    }
                  });
                }}
              />
            </S.AddingMethodsList>
          </S.AddingMethodsBody>
        </S.AddingMethods>
        {addingMethods[formik.values.sharingMethod].badge && (
          <Notification
            status="warning"
            style={{ marginBottom: 24 }}
            message={
              <Typography variant="body2">
                {addingMethods[formik.values.sharingMethod].badge}
              </Typography>
            }
          />
        )}
        {formik.values.sharingMethod === CONTACT_TYPE.list ? (
          <WithContacts automation={automation} onUpdate={onUpdate} />
        ) : (
          <WithTags automation={automation} onUpdate={onUpdate} />
        )}
        {isMissingInfoModalOpen && automation && (
          <MissingRecipientInfoModal
            isOpen={isMissingInfoModalOpen}
            onRequestClose={setIsMissingInfoModalOpen}
            contacts={missingInfoContacts}
            occasionType={automation?.type}
            disabledColumns={[
              automation?.type === 'birthday' ? 'anniversary' : 'birthday',
              automation?.contacts?.tags?.length > 0 && 'actions'
            ]}
            deleteContact={id => {
              onUpdate({
                params: {
                  code
                },
                body: {
                  contacts: {
                    ids: automation?.contacts?.ids?.filter(contact => contact.id !== id)
                  }
                }
              });
            }}
          />
        )}
      </CardContent>
    </>
  );
}

export { AutomationReviewContactsAccordion };
